import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { BurialPlotsSelectBtn } from "./burialPlotsSelectBtn";
import { TableInput } from "components/common/input";
import { BurialPlotDetailTable } from "./burialPlotDetailTable";
import { BurialPlotList } from "service/constant/burialConstant";
import { IBurialPlotsName } from "service/interface/burial";
import { IBurialDetail, IBurialPlots } from "service/interface/burial";

interface Props {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  data: IBurialDetail;
  setData: React.Dispatch<React.SetStateAction<IBurialDetail>>;
  selectIdx: string;
}

export function BurialPlotsModal({
  isVisible,
  setIsVisible,
  data,
  setData,
  selectIdx,
}: Props) {
  const [modalData, setModalData] = useState<IBurialPlots>({
    ...({} as IBurialPlots),
    id: 0,
    name: "",
    nameNum: 0,
    BurialPlotTypes: [
      {
        BurialPlotTypeDetails: [
          {
            id: 0,
            name: "",
            data: [],
          },
        ],
        id: 0,
        name: "",
        nameNum: 0,
      },
    ],
  });
  const [plotJson, setPlotJson] = useState<
    IBurialPlots["BurialPlotTypes"][0]["BurialPlotTypeDetails"][0]["data"]
  >([]);

  useEffect(() => {
    if (isVisible && selectIdx) {
      setModalData(data.BurialPlots[Number(selectIdx)]);
    } else if (isVisible && !selectIdx) {
      setModalData({
        ...({} as IBurialPlots),
        name: "",
        nameNum: 0,
        BurialPlotTypes: [
          {
            BurialPlotTypeDetails: [
              {
                id: 0,
                name: "",
                data: [],
              },
            ],
            id: 0,
            name: "",
            nameNum: 0,
          },
        ],
      });
    }
  }, [isVisible]);

  const handleCancelBtn = () => {
    setIsVisible(false);
  };

  const handleLargeClick = (item: IBurialPlotsName) => {
    setModalData((prev) => ({
      ...prev,
      name: item.name,
      nameNum: item.nameNum,
      BurialPlotTypes: prev.BurialPlotTypes
        ? [
            {
              ...prev.BurialPlotTypes[0],
              name: "",
              nameNum: 0,
              id: 0,
            },
          ]
        : [
            {
              BurialPlotTypeDetails: [],
              name: "",
              nameNum: 0,
              id: 0,
            },
          ],
    }));
  };

  const handleMiddleClick = (item: IBurialPlotsName) => {
    setModalData((prev) => ({
      ...prev,
      BurialPlotTypes: [
        {
          ...prev.BurialPlotTypes[0],
          name: item.name,
          nameNum: item.nameNum,
        },
      ],
    }));
  };

  const handleSmallNameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModalData((prev) => ({
      ...prev,
      BurialPlotTypes: [
        {
          ...prev.BurialPlotTypes[0],
          BurialPlotTypeDetails: [
            {
              ...prev.BurialPlotTypes[0].BurialPlotTypeDetails[0],
              name: e.target.value,
            },
          ],
        },
      ],
    }));
  };

  const handleSaveBtn = () => {
    if (selectIdx) {
      let copiedArr = [...data.BurialPlots];
      copiedArr[Number(selectIdx)] = {
        ...modalData,
        BurialPlotTypes: [
          {
            ...modalData.BurialPlotTypes[0],
            BurialPlotTypeDetails: [
              {
                ...modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0],
                name: modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0].name
                  ? modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0].name
                  : modalData.BurialPlotTypes[0].name,
                data: plotJson,
              },
            ],
          },
        ],
      };
      setData((prev) => ({
        ...prev,
        BurialPlots: copiedArr,
      }));
    } else {
      setData((prev) => ({
        ...prev,
        BurialPlots: [
          ...prev.BurialPlots,
          {
            ...modalData,
            BurialPlotTypes: [
              {
                ...modalData.BurialPlotTypes[0],
                BurialPlotTypeDetails: [
                  {
                    ...modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0],
                    name: modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0]
                      .name
                      ? modalData.BurialPlotTypes[0].BurialPlotTypeDetails[0]
                          .name
                      : modalData.BurialPlotTypes[0].name,
                    data: plotJson,
                  },
                ],
              },
            ],
          },
        ],
      }));
    }
    setIsVisible(false);
  };

  return (
    <Transition.Root show={isVisible} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[999999999999]"
        onClose={handleCancelBtn}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 overflow-scroll bg-black bg-opacity-80 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-scroll">
          <div className="flex min-h-full min-w-[1131px] items-center justify-center p-[20px]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="transition-al relative mx-auto h-fit w-[1031px] min-w-[1031px] transform overflow-scroll rounded-[12px] bg-white px-[48px] pb-[96px] pt-[56px]">
                <div className="mb-[32px] flex items-center gap-[16px]">
                  <div className="m-0 flex h-[32px] w-[32px] items-center justify-center rounded-full bg-black text-[22px] font-extrabold text-white">
                    1
                  </div>
                  <p className="m-0 text-[24px] font-semibold text-black">
                    대분류 선택하기
                  </p>
                </div>
                <p className="m-0 text-[20px] font-semibold text-black">
                  장지 종류
                </p>
                <div className="mb-[58px] mt-[24px] w-full border-t-[2px] border-t-black p-0 pt-[24px]">
                  <div className="flex items-center gap-[20px]">
                    {BurialPlotList.map((list, idx) => (
                      <BurialPlotsSelectBtn
                        label={list}
                        onClick={handleLargeClick}
                        isSelect={list.nameNum === modalData.nameNum}
                      />
                    ))}
                  </div>
                </div>
                <div className="mb-[32px] flex items-center gap-[16px]">
                  <div className="m-0 flex h-[32px] w-[32px] items-center justify-center rounded-full bg-black text-[22px] font-extrabold text-white">
                    2
                  </div>
                  <p className="m-0 text-[24px] font-semibold text-black">
                    중분류 선택하기
                  </p>
                </div>
                <p className="m-0 text-[20px] font-semibold text-black">
                  유형 분류
                </p>
                <div className="mb-[58px] mt-[24px] w-full border-t-[2px] border-t-black p-0 pt-[24px]">
                  <div className="flex items-center gap-[20px]">
                    {BurialPlotList[modalData.nameNum - 1]?.chlid.map(
                      (middle, idx) => (
                        <BurialPlotsSelectBtn
                          label={middle}
                          onClick={handleMiddleClick}
                          isSelect={
                            modalData?.BurialPlotTypes
                              ? middle.nameNum ===
                                modalData?.BurialPlotTypes[0].nameNum
                              : false
                          }
                        />
                      ),
                    )}
                  </div>
                </div>
                <div className="mb-[32px] flex items-center gap-[16px]">
                  <div className="m-0 flex h-[32px] w-[32px] items-center justify-center rounded-full bg-black text-[22px] font-extrabold text-white">
                    3
                  </div>
                  <p className="m-0 text-[24px] font-semibold text-black">
                    소분류 선택하기
                  </p>
                </div>
                <p className="m-0 text-[20px] font-semibold text-black">
                  세부 분류
                </p>
                <div className="mb-[58px] mt-[24px] w-full border-t-[2px] border-t-black p-0">
                  <div className="mb-[24px] flex h-[86px] w-full border-b-[1px] border-b-gray-1">
                    <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
                      분류 이름
                    </div>
                    <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
                      <TableInput
                        value={
                          modalData?.BurialPlotTypes[0]
                            ?.BurialPlotTypeDetails[0].name
                        }
                        onChange={handleSmallNameInput}
                        placeholder="이름을 지정해 주세요. 없을 시, 중분류의 이름이 그대로 기입됩니다."
                      />
                    </div>
                  </div>
                  <BurialPlotDetailTable
                    plotJson={plotJson}
                    setPlotJson={setPlotJson}
                    data={data}
                    isVisible={isVisible}
                    selectIdx={selectIdx}
                  />
                </div>
                <div className="mt-[56px] flex items-center gap-[57px]">
                  <button
                    onClick={handleCancelBtn}
                    className="h-[98px] w-[439px] rounded-[16px] bg-[#ececec] text-[18px] font-semibold text-[#646464]"
                  >
                    취소하기
                  </button>
                  <button
                    onClick={handleSaveBtn}
                    disabled={
                      !modalData.nameNum ||
                      !modalData.BurialPlotTypes[0].nameNum
                    }
                    className="h-[98px] w-[439px] rounded-[16px] bg-[#585858] text-[18px] font-semibold text-[#fff] disabled:cursor-not-allowed disabled:bg-gray-1"
                  >
                    저장하기
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
